/**
 * Which movement a recording question asks the client to film.
 *
 * A recording question cannot exist without one — `CustomQuestionRules.Validate` refuses an
 * `ExerciseClip` that names no exercise, and `EnsureNamedExercisesAreVisibleAsync` refuses one
 * naming a movement this trainer cannot see. So the picker stands BETWEEN writing the question and
 * adding it: the question waits, the movement is chosen, and only then does the row appear.
 *
 * ── It IS the plan builder's library (owner, 17 September 2026) ──────────────────────────────
 * It was a bare name-filtered list capped at 40 rows, and this header used to record the reasoning:
 * „a trainer picking the movement they were already thinking of types its name, and four facet
 * groups in a 460px dialog would be the rail rebuilt inside a modal". **The owner overruled it** —
 * „biblioteka ćwiczeń z buildera planów w mniejszej formie … wyciągnij ją do wspólnego komponentu,
 * filtry i szukajka 1:1" — so the library moved into `entities/exercise-library` and this dialog
 * draws its `dialog` variant. Same field, same four facets, same behaviour, and the cap is gone: the
 * list scrolls inside a box whose height is fixed, so all 477 movements are reachable.
 *
 * One departure from the drawer, and it is the owner's: **the rows carry no muscle caption.** A
 * trainer naming the one movement a question is about is not reading the list by body part, and the
 * `Mięśnie` tab above is where they go when they are.
 */

import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ExerciseLibraryList,
  useExerciseLibraryQuery,
  type ExerciseFacetLabels,
  type ExerciseLibraryListLabels,
} from '~/entities/exercise-library';
import { useSessionUser } from '~/entities/session';
import { displayInitials } from '~/shared/lib/displayInitials';

import type { AuthoredQuestion } from '../model/editorDraft';
import type { TranslateKey } from '../model/translateKey';
import { PickerDialog } from './PickerDialog';

/**
 * The library's own copy, reused verbatim.
 *
 * The keys live under the plan builder's namespace because that is where the surface was authored,
 * and the words are the library's rather than the builder's — „Szukaj ćwiczenia", „Filtry",
 * „Wyczyść wszystkie", „Nie udało się wczytać biblioteki ćwiczeń". Re-keying them under
 * `web:reports` would put the same fourteen strings in the locale file twice and let the two drift;
 * this dialog's OWN copy — its title, its lede and what a press means — stays under `web:reports`.
 */
const LIB = 'web:planBuilder.rail';

export interface ExercisePickerProps {
  /** The question waiting on a movement, or `null` when the picker is closed. */
  readonly pending: AuthoredQuestion | null;
  readonly onChoose: (exerciseId: number, name: string) => void;
  readonly onClose: () => void;
  readonly triggerRef: RefObject<HTMLElement | null>;
  readonly t: TranslateKey;
}

export function ExercisePicker({
  pending,
  onChoose,
  onClose,
  triggerRef,
  t,
}: ExercisePickerProps) {
  const { t: translate } = useTranslation();
  const user = useSessionUser();
  // Disabled until the dialog is open: the catalogue is the app's largest read and the editor must
  // not fetch it for a form that holds no recording question.
  const library = useExerciseLibraryQuery({ enabled: pending !== null });
  const isOpen = pending !== null;

  // `model/` may not import i18next, so the four facets are handed their words. Equipment is
  // absent: its chips carry backend DATA that arrives already localised. The `lang` prop this
  // component used to take went with the read: `useExerciseLibraryQuery` sends the trainer's chosen
  // CONTENT language, which is the one authority for it.
  const facets: ExerciseFacetLabels = {
    group: (facet) => translate(`${LIB}.facet.${facet}`),
    muscle: (muscle) => translate(`exerciseLibrary.muscles.${muscle}`),
    type: (type) => translate(`web:planBuilder.day.exerciseType.${type}`),
    origin: (origin) => translate(`${LIB}.origin.${origin}`),
    everything: translate(`${LIB}.anyValue`),
  };

  const labels: ExerciseLibraryListLabels = {
    search: translate(`${LIB}.search`),
    searchPlaceholder: translate(`${LIB}.searchPlaceholder`),
    filters: translate(`${LIB}.filters`),
    filtersWithCount: (count) => translate(`${LIB}.filtersWithCount`, { count }),
    removeFilter: (label) => translate(`${LIB}.removeFilter`, { label }),
    clearAll: translate(`${LIB}.clearAll`),
    clearAllName: (count) => translate(`${LIB}.clear`, { count }),
    count: (count) => translate(`${LIB}.drawerCount`, { count }),
    results: (count) => translate(`${LIB}.results`, { count }),
    loading: translate(`${LIB}.loading`),
    error: translate(`${LIB}.error`),
    retry: translate(`${LIB}.retry`),
    empty: translate(`${LIB}.empty`),
    noMatches: translate(`${LIB}.noMatches`),
    noMatchesHint: translate(`${LIB}.noMatchesHint`),
    otherLanguageFailed: translate('web:exerciseLibrary.language.otherFailed'),
    // The one row label that is this SCREEN's rather than the library's: a press here names the
    // movement a question asks about, it does not add anything to a day.
    rowAction: (name) => t('web:reports.editor.exercises.choose', { name }),
    muscle: (muscle) => translate(`exerciseLibrary.muscles.${muscle}`),
    type: (type) => translate(`web:planBuilder.day.exerciseType.${type}`),
    metaSeparator: translate(`${LIB}.metaSeparator`),
    facets,
    panel: {
      tab: (tab) => translate(`${LIB}.tab.${tab}`),
      tabWithCount: (label, count) => translate(`${LIB}.tabWithCount`, { label, count }),
      panel: translate(`${LIB}.filters`),
      optionName: (label, count) => translate(`${LIB}.facetOption`, { label, count }),
      optionSearch: (facet) => translate(`${LIB}.optionSearch.${facet}`),
      originHint: (id) => translate(`${LIB}.originHint.${id}`),
      empty: translate(`${LIB}.facetEmpty`),
      clear: translate(`${LIB}.clearShort`),
      apply: translate(`${LIB}.apply`),
    },
  };

  return (
    <PickerDialog
      open={isOpen}
      onClose={onClose}
      title={t('web:reports.editor.exercises.title')}
      lede={t('web:reports.editor.exercises.lede')}
      wide
      triggerRef={triggerRef}
      t={t}
    >
      <ExerciseLibraryList
        library={library}
        variant="dialog"
        /* The survey editor's draft is not keyed by the content language, so switching it here
         * re-reads the catalogue and the clip question's name and touches nothing the trainer wrote. */
        withLanguageTab
        labels={labels}
        trainer={{
          initials: displayInitials(user?.displayName ?? ''),
          avatarUrl: user?.avatarUrl ?? null,
        }}
        /* A constant, and it still focuses on every open: Base UI unmounts the popup when the
         * dialog closes, so the field is a fresh element each time and `autoFocus` fires again. The
         * drawer's counter exists because its host keeps the rail mounted; this one does not. */
        focusSignal={1}
        // A press NAMES the movement and closes; there is no second choice to mark, so no ticks.
        onPick={(item) => {
          // The next question starts on the whole catalogue. The picker outlives one reach — it is
          // rendered by the form whether or not a question is waiting — so a query left standing
          // would open the next recording question on the last one's filters.
          library.clear();
          library.setText('');
          onChoose(item.id, item.name);
        }}
      />
    </PickerDialog>
  );
}
